import type { ReactNode } from "react";
import { AccountFactsSection } from "@williampollardco/portal-ui";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  PAGE_WORDING,
  ordersReachYou,
} from "@shared-core/domain/supplier-portal-wording";
import { LoadFailed, LoadingCard } from "../shell/states";

const LOGIN = { via: "login" } as const;

/**
 * `/account` (login only): read-only in v1. Pollards staff manage who has a login. The `name` of each person with access is
 * their label, else their address: a login has no other name (docs/features/portal-sign-in/07, section 11). `before` is the shared "Your sign-in"
 * section (docs/features/portal-sign-in/09, 5.8), which comes first on the one Account page both portals have.
 */
export function AccountPage({ before = null }: { before?: ReactNode } = {}) {
  const adapter = useSupplierPortalAdapter();
  const {
    data: account,
    error,
    reload,
  } = useLoad(() => adapter.getAccount(LOGIN), "account");

  if (!account) {
    return (
      <>
        <h1 className="text-lg font-semibold">Account</h1>
        {before}
        {error ? (
          <LoadFailed
            what="your account"
            message={mutationErrorMessage(
              error,
              "Something went wrong reaching Pollards.",
            )}
            onRetry={reload}
          />
        ) : (
          <LoadingCard what="your account" />
        )}
      </>
    );
  }

  return (
    <>
      <h1 className="text-lg font-semibold">Account</h1>
      {before}
      <AccountFactsSection
        heading="Your company"
        title={account.supplier_name}
        facts={[
          { term: "Supplier number", value: account.supplier_no },
          {
            term: "Orders reach you",
            value: ordersReachYou(account.orders_reach_you),
          },
          {
            term: "People with access",
            value: account.people.map((p) => p.name).join(" · "),
          },
        ]}
        note={PAGE_WORDING.accountManaged(account.pollards_contact.name)}
      />
    </>
  );
}
